import type {
  CareerExplorerRecommendation,
  CareerExplorerRecommendationDetail,
  CareerExplorerRecommendationsProps,
} from '@highereducation/hen-ui';

import type { CareerProgram } from '../types';
import {
  DEFAULT_CTA_LABEL,
  DEFAULT_TITLE,
  DEFAULT_VIEW_ALL_HREF,
  DEFAULT_VIEW_ALL_LABEL,
  FALLBACK_PARTNER_LOGO,
} from './constants';

export type RecommendationReasonInput = {
  careerLabel: string;
  goalLabel: string | null;
  levelLabel: string | null;
};

export type MapCareerRecommendationsInput = {
  programs: CareerProgram[];
  careerLabel: string;
  goalLabel: string | null;
  levelLabel: string | null;
  title?: string;
  viewAllLabel?: string;
  viewAllHref?: string;
};

export function buildRecommendationReason({
  careerLabel,
  goalLabel,
  levelLabel,
}: RecommendationReasonInput): string {
  const parts = [careerLabel, goalLabel, levelLabel].filter(
    (part): part is string => Boolean(part),
  );

  return `Based on ${parts.join(', ')}`;
}

function mapProgram(program: CareerProgram): CareerExplorerRecommendation {
  const levelDetails: CareerExplorerRecommendationDetail[] = program.level
    ? [{ icon: 'gauge', label: program.level }]
    : [];
  const skillDetails: CareerExplorerRecommendationDetail[] = (
    program.skills ?? []
  ).map((skill) => ({ icon: 'check', label: skill }));
  const details = [...levelDetails, ...skillDetails];

  return {
    id: program.id,
    title: program.title,
    partnerLogoUrl: program.imageUrl || FALLBACK_PARTNER_LOGO,
    partnerLogoAlt: program.partnerName,
    description: program.description ?? '',
    details: details.length > 0 ? details : undefined,
    ctaLabel: program.cardCtaLabel || DEFAULT_CTA_LABEL,
    ctaHref: program.url,
  };
}

export function mapCareerRecommendations({
  programs,
  careerLabel,
  goalLabel,
  levelLabel,
  title = DEFAULT_TITLE,
  viewAllLabel = DEFAULT_VIEW_ALL_LABEL,
  viewAllHref = DEFAULT_VIEW_ALL_HREF,
}: MapCareerRecommendationsInput): CareerExplorerRecommendationsProps {
  return {
    title,
    recommendationReason: buildRecommendationReason({
      careerLabel,
      goalLabel,
      levelLabel,
    }),
    programs: programs.map(mapProgram),
    viewAllLabel,
    viewAllHref,
  };
}
